import type { InputHTMLAttributes, LabelHTMLAttributes, ReactNode, Ref, TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

const FIELD =
  'w-full rounded-md border border-border-strong bg-surface text-fg placeholder:text-fg-subtle ' +
  'transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/25 ' +
  'disabled:opacity-60 aria-[invalid=true]:border-danger';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  ref?: Ref<HTMLInputElement>;
  /** Borderless variant for inline editing inside dense rows. */
  bare?: boolean;
}

export function Input({ className, bare, ...props }: InputProps) {
  return (
    <input
      className={cn(
        bare
          ? 'w-full bg-transparent text-fg placeholder:text-fg-subtle focus:outline-none'
          : cn(FIELD, 'h-8 px-2.5 text-sm'),
        className,
      )}
      {...props}
    />
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  ref?: Ref<HTMLTextAreaElement>;
}

export function Textarea({ className, ...props }: TextareaProps) {
  return <textarea className={cn(FIELD, 'min-h-20 px-2.5 py-1.5 text-sm', className)} {...props} />;
}

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn('mb-1 block text-sm font-medium text-fg', className)} {...props} />;
}

/** Label, control and error text stacked. */
export function Field({
  label,
  error,
  hint,
  htmlFor,
  children,
  className,
}: {
  label?: ReactNode;
  error?: string | null;
  hint?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      {label ? <Label htmlFor={htmlFor}>{label}</Label> : null}
      {children}
      {error ? (
        <p role="alert" className="mt-1 text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-fg-muted">{hint}</p>
      ) : null}
    </div>
  );
}
